/**
 * The persona subpanel inside the account menu (#2328 §2, ROOT ruling 74071 §7).
 *
 * ── it is a SUBPANEL, not a second menu ────────────────────────────────────
 * #2328 §2: *"Selecting the trigger opens the existing account menu extended by
 * a dedicated persona subpanel."* So this file is composed by `UserMenu` and is
 * deliberately NOT on the `@solum/ui` barrel: a second exported persona surface
 * is how the console and the portal end up with two of them, which is the one
 * failure `ui-and-experience.md` §1 exists to prevent. The console binds it
 * through `UserMenu`'s `persona` prop; the portal passes nothing and renders no
 * section at all, because a host that should not offer an affordance does not
 * pass the prop (never a no-op handler).
 *
 * ── DATA-FREE, like everything else in this package ────────────────────────
 * Every list arrives as a prop. This component does not know what a persona IS,
 * does not fetch one, cannot say which ones an actor may activate, and holds no
 * preference. `packages/ui` may not import `@solum/sdk` — the moment it does it
 * stops being importable by a host with a different data door.
 *
 * ── IDENTITY IS THE KEY (#2328 §2) ─────────────────────────────────────────
 * Every callback carries `key`. A label is translated and a translated label is
 * not an identity — two deployments can localise `Delivery` differently and a
 * preference keyed on the rendered string would follow neither.
 *
 * ── WHAT IS DELIBERATELY NOT RENDERED, AND WHY ─────────────────────────────
 *   · **No warning / "viewing as" alarm state.** #2328 §2 allows *"a distinct
 *     warning/state only when the system is genuinely simulating or delegating
 *     another identity/persona"*, and measured on this base there is no such
 *     mechanism: an account switch in this shell is a full sign-out and
 *     re-authentication (`packages/auth/src/accounts.ts`), and selecting a
 *     persona changes which experience is resolved, never who the caller is.
 *     So NO warning is rendered rather than a decorative one — ordinary persona
 *     selection is not impersonation and must not be dressed as it. The
 *     `aria-label` on the acting row says *changes experience, not permissions*
 *     for the same reason, and that sentence is pinned by a test.
 *   · **No description where the deployment has none.** `core.persona` carries
 *     `key`, `label`, `sort`, `origin`, `is_active` and nothing else, so the
 *     governed short description #2328 asks for has no column to come from yet.
 *     The prop exists and is rendered when a host supplies one; nothing is
 *     fabricated. The gap is reported with the lane, not filled here.
 *   · **No "recently used" section without governed history.** `recent` absent
 *     renders no heading — an empty section is a promise the deployment has not
 *     made.
 */
import { useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { Check, MoreVertical, Pin, PinOff, Star } from 'lucide-react'
import { cx } from '../controls/cx'
import type { PersonaChoice, UserMenuPersona } from './UserMenu'
import s from './PersonaPanel.module.css'

/* The two shapes are declared in `UserMenu.tsx`, not here, and the reason is
   #2022's disposition census rather than taste: a type is public API of the
   VALUE it describes, and the adopted value is `UserMenu`. Declaring them in a
   file the barrel does not export would leave two orphan types that no host
   imports at runtime and no disposition owns. Type-only, so the import is
   erased and there is no runtime cycle. */
export type { PersonaChoice, UserMenuPersona }

/**
 * The search, as a pure function so it can be DRIVEN.
 *
 * Indexes name, governed description and declared synonyms — #2328 §2's exact
 * list. It does NOT index the key: a key is an identity, not a label, and
 * matching on it would make the untranslated internal spelling a search term
 * users see ranked above the one they typed.
 */
export function matchPersonas(
  choices: readonly PersonaChoice[], query: string,
): PersonaChoice[] {
  const q = query.trim().toLocaleLowerCase()
  if (q === '') return [...choices]
  return choices.filter((choice) => {
    const haystack = [choice.label, choice.description ?? '', ...(choice.synonyms ?? [])]
    return haystack.some((text) => text.toLocaleLowerCase().includes(q))
  })
}

/** Keys in `order` that are present in `choices`, in that order, deduplicated. */
function pick(
  choices: readonly PersonaChoice[], order: readonly string[] | undefined,
): PersonaChoice[] {
  if (!order || order.length === 0) return []
  const byKey = new Map(choices.map((choice) => [choice.key, choice]))
  const seen = new Set<string>()
  const out: PersonaChoice[] = []
  for (const key of order) {
    const choice = byKey.get(key)
    if (!choice || seen.has(key)) continue
    seen.add(key)
    out.push(choice)
  }
  return out
}

/**
 * The row overflow — Pin/Unpin and Set as default (#2328 §2).
 *
 * Hand-rolled rather than the shared `Menu`: that control portals to
 * `document.body`, and this panel lives inside a menu whose own outside-click
 * handler would read a click in the portal as an outside click and close
 * everything under the person's cursor. Rendered in place, the disclosure is
 * inside the panel it belongs to and the existing contract holds unchanged.
 *
 * Escape closes THIS disclosure and stops there — the account menu's own
 * document-level Escape handler would otherwise close the whole menu, which is
 * two dismissals for one press.
 */
function RowMenu({ label, pinned, isDefault, onPin, onUnpin, onSetDefault }: {
  label: string
  pinned: boolean
  isDefault: boolean
  onPin?: (() => void) | undefined
  onUnpin?: (() => void) | undefined
  onSetDefault?: (() => void) | undefined
}) {
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  const canPin = pinned ? Boolean(onUnpin) : Boolean(onPin)
  if (!canPin || !onSetDefault) return null
  const close = () => { setOpen(false); trigger.current?.focus() }
  /* Escape closes THIS disclosure and stops there — the account menu's own
     document-level Escape handler would otherwise close the whole menu, which
     is two dismissals for one press. Bound to the BUTTONS rather than to a
     wrapper: while this is open, focus is on the trigger or on an item, both of
     which are native interactive elements. A keydown on a plain wrapper would
     be a static element claiming an interaction it cannot receive by keyboard. */
  const onEscape = (e: ReactKeyboardEvent) => {
    if (e.key !== 'Escape' || !open) return
    e.stopPropagation()
    e.preventDefault()
    close()
  }
  return (
    <span className={s.rowMenu}>
      <button ref={trigger} type="button" className={s.rowMore}
        aria-haspopup="menu" aria-expanded={open}
        aria-label={`More options for ${label}`}
        data-testid="persona-row-more"
        onKeyDown={onEscape}
        onClick={() => setOpen((v) => !v)}>
        <MoreVertical size={14} aria-hidden />
      </button>
      {open && (
        <span role="menu" className={s.rowMenuList} aria-label={`${label} options`}
          data-testid="persona-row-menu">
          <button type="button" role="menuitem" className={s.rowMenuItem}
            data-testid="persona-row-pin" onKeyDown={onEscape}
            onClick={() => { close(); if (pinned) onUnpin?.(); else onPin?.() }}>
            {pinned ? <PinOff size={13} aria-hidden /> : <Pin size={13} aria-hidden />}
            {pinned ? 'Unpin' : 'Pin'}
          </button>
          <button type="button" role="menuitem" className={s.rowMenuItem}
            disabled={isDefault} data-testid="persona-row-default" onKeyDown={onEscape}
            onClick={() => { close(); onSetDefault() }}>
            <Star size={13} aria-hidden />
            {isDefault ? 'Your default' : 'Set as default'}
          </button>
        </span>
      )}
    </span>
  )
}

function PersonaRow({ choice, acting, pinned, isDefault, persona }: {
  choice: PersonaChoice
  acting: boolean
  pinned: boolean
  isDefault: boolean
  persona: UserMenuPersona
}) {
  return (
    <span className={cx(s.row, acting && s.rowOn)} data-persona={choice.key}>
      <button type="button" className={s.rowPick}
        data-testid="persona-choice" data-persona-key={choice.key}
        aria-current={acting ? 'true' : undefined}
        aria-label={acting
          ? `Acting as ${choice.label}; changes experience, not permissions`
          : `Act as ${choice.label}`}
        onClick={() => persona.onActivate(choice.key)}>
        <span className={s.rowLabel}>
          {choice.label}
          {acting && <Check size={13} aria-hidden data-testid="persona-acting-check" />}
        </span>
        {choice.description ? <span className={s.rowNote}>{choice.description}</span> : null}
      </button>
      <RowMenu label={choice.label} pinned={pinned} isDefault={isDefault}
        onPin={persona.onPin ? () => persona.onPin!(choice.key) : undefined}
        onUnpin={persona.onUnpin ? () => persona.onUnpin!(choice.key) : undefined}
        onSetDefault={persona.onSetDefault ? () => persona.onSetDefault!(choice.key) : undefined} />
    </span>
  )
}

export function PersonaPanel({ persona }: { persona: UserMenuPersona }) {
  const [query, setQuery] = useState('')
  const active = persona.active ?? ''
  const pinnedKeys = useMemo(() => new Set(persona.pinned ?? []), [persona.pinned])

  const matches = useMemo(
    () => matchPersonas(persona.choices, query), [persona.choices, query])
  const acting = useMemo(
    () => persona.choices.find((choice) => choice.key === active) ?? null,
    [persona.choices, active])

  /* The acting persona is pinned at the top and is NOT repeated below, the way
     `personaOptions` keeps an acting value visible: a list that shows the same
     persona twice makes the selected one ambiguous. */
  const rest = matches.filter((choice) => choice.key !== active)
  const pinnedRows = pick(rest, persona.pinned)
  const recentRows = pick(rest, persona.recent)
    .filter((choice) => !pinnedKeys.has(choice.key))
  const shown = new Set([...pinnedRows, ...recentRows].map((choice) => choice.key))
  const others = rest.filter((choice) => !shown.has(choice.key))

  const row = (choice: PersonaChoice) => (
    <PersonaRow key={choice.key} choice={choice} acting={choice.key === active}
      pinned={pinnedKeys.has(choice.key)}
      isDefault={persona.defaultKey === choice.key} persona={persona} />
  )

  return (
    <div className={s.persona} data-testid="user-menu-persona">
      <span className={s.label} id="persona-panel-label">Persona</span>

      {acting && (
        <div className={s.current} data-testid="persona-current">
          {row(acting)}
        </div>
      )}

      {persona.choices.length > 1 && (
        <input className={s.search} type="search" value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search personas"
          aria-label="Search personas"
          data-testid="persona-search" />
      )}

      <div className={s.list} role="group" aria-labelledby="persona-panel-label">
        {pinnedRows.length > 0 && (
          <>
            <span className={s.group} data-testid="persona-group-pinned">Pinned</span>
            {pinnedRows.map(row)}
          </>
        )}
        {recentRows.length > 0 && (
          <>
            <span className={s.group} data-testid="persona-group-recent">Recently used</span>
            {recentRows.map(row)}
          </>
        )}
        {others.length > 0 && (
          <>
            {(pinnedRows.length > 0 || recentRows.length > 0) && (
              <span className={s.group} data-testid="persona-group-rest">All personas</span>
            )}
            {others.map(row)}
          </>
        )}
        {matches.length === 0 && (
          <p className={s.none} data-testid="persona-no-match">
            No persona matches that.
          </p>
        )}
      </div>
    </div>
  )
}
